import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Drawer,
  TextareaAutosize,
  Divider,
  Paper,
} from '@mui/material';
import { X, CaretUp, CaretDown } from '@phosphor-icons/react';
import { useAlert } from '../../hooks/useAlert';

interface QuestionEditDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedStore: any;
  questionData: {
    id?: number;
    question: string;
    answer: string;
  };
  questionIndex: number;
  onSave: (question: { question: string; answer: string }) => void;
  isPending: boolean;
}

const QuestionEditDrawer: React.FC<QuestionEditDrawerProps> = ({
  open,
  onClose,
  questionData,
  questionIndex,
  onSave,
  isPending,
}) => {
  const [question, setQuestion] = useState({
    question: '',
    answer: '',
    isOpen: true,
  });
  const [isOpen, setIsOpen] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { showAlert } = useAlert();

  useEffect(() => {
    if (open && questionData) {
      setQuestion({
        question: questionData.question,
        answer: questionData.answer,
        isOpen: true,
      });
    }
  }, [open, questionData]);

  const handleSave = () => {
    try {
      setIsSaving(true);
      if (!question.question || !question.answer) {
        showAlert('Please fill in both question and answer fields', 'error');
        return;
      }
      onSave(question);
      onClose();
    } catch (error) {
      console.error('Error saving question:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: '600px',
          padding: '20px 24px',
          backgroundColor: '#FAFBFD',
          overflow: 'auto',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              '&:hover': { backgroundColor: '#E8EBF1' },
              transition: 'all 0.2s ease',
            }}
          >
            <X size={18} />
          </IconButton>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 18,
              color: 'custom.textBlue',
              letterSpacing: '-0.2px',
            }}
          >
            Edit Question
          </Typography>
        </Box>

        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isSaving || isPending}
          sx={{
            height: '34px',
            fontSize: 14,
            fontWeight: 500,
            borderRadius: '8px',
            textTransform: 'none',
            boxShadow: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
            },
          }}
        >
          {isSaving || isPending ? 'Saving...' : 'Save'}
        </Button>
      </Box>

      <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.08)', mb: 2, mx: -3 }} />

      <Paper
        elevation={0}
        sx={{
          bgcolor: '#fff',
          border: '1px solid',
          borderColor: '#DCE0E7',
          borderRadius: '6px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            bgcolor: '#5A4CE61A',
            cursor: 'pointer',
          }}
          onClick={() => setIsOpen(!isOpen)}
        >
          <Typography
            sx={{ fontSize: '14px', fontWeight: 500, color: '#1C1C1C' }}
          >
            Question {questionIndex + 1}
          </Typography>
          <IconButton
            size="small"
            sx={{
              minWidth: '30px',
              height: '30px',
              p: 0,
              borderRadius: '100%',
              bgcolor: '#FEFEFE',
              '&:hover': { bgcolor: '#FEFEEE' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isOpen ? <CaretUp size={16} /> : <CaretDown size={16} />}
          </IconButton>
        </Box>

        {isOpen && (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '100%',
                  letterSpacing: '-1%',
                  color: 'text.secondary',
                  mb: 0.5,
                }}
              >
                Question
              </Typography>
              <TextareaAutosize
                placeholder="Enter your question here"
                value={question.question}
                onChange={e =>
                  setQuestion({ ...question, question: e.target.value })
                }
                minRows={1}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: '1px solid #DCE0E7',
                  borderRadius: '6px',
                  fontFamily: 'inherit',
                  resize: 'none',
                  outline: 'none',
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '100%',
                  letterSpacing: '-1%',
                  color: 'text.secondary',
                  mb: 0.5,
                }}
              >
                Answer
              </Typography>
              <TextareaAutosize
                placeholder="Enter your answer here"
                value={question.answer}
                onChange={e =>
                  setQuestion({ ...question, answer: e.target.value })
                }
                minRows={1}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: '1px solid #DCE0E7',
                  borderRadius: '6px',
                  fontFamily: 'inherit',
                  resize: 'none',
                  outline: 'none',
                }}
              />
            </Box>
          </Box>
        )}
      </Paper>
    </Drawer>
  );
};

export default QuestionEditDrawer;
